import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const brand = '#0D9488';
const steps = ['Agency Info', 'Admin Account', 'Branding', 'Done'];

interface FormData {
  agencyName: string;
  country: string;
  city: string;
  website: string;
  phone: string;
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  brandColor: string;
  currency: string;
  plan: string;
}

const colorPresets = ['#0D9488', '#059669', '#DC2626', '#D97706', '#9333EA', '#0369A1', '#1D4ED8', '#BE185D'];

const plans = [
  { id: 'starter', name: 'Starter', price: 'AED 199/mo', features: ['5 Staff', '500 Leads', 'Basic Reports'] },
  { id: 'pro', name: 'Pro', price: 'AED 499/mo', features: ['20 Staff', 'Unlimited Leads', 'AI Assistant', 'WhatsApp'], popular: true },
  { id: 'enterprise', name: 'Enterprise', price: 'Custom', features: ['Unlimited Staff', 'Custom Domain', 'Dedicated Support'] },
];

const validPlanIds = ['starter', 'pro', 'enterprise'];

const REGISTER_EDGE_FN = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/register-agency`;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPlan = searchParams.get('plan') ?? '';
  const initialPlan = validPlanIds.includes(urlPlan) ? urlPlan : 'starter';

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [registeredAgencyName, setRegisteredAgencyName] = useState('');

  const [form, setForm] = useState<FormData>({
    agencyName: '',
    country: '',
    city: '',
    website: '',
    phone: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    brandColor: '#0D9488',
    currency: 'AED',
    plan: initialPlan,
  });

  const update = (key: keyof FormData, val: string) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const validateStep = (): string => {
    if (step === 0) {
      if (!form.agencyName.trim()) return 'Agency name is required';
      if (!form.country) return 'Please select a country';
      if (!form.city.trim()) return 'City is required';
    }
    if (step === 1) {
      if (!form.fullName.trim()) return 'Full name is required';
      if (!form.email.trim()) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email';
      if (!form.password) return 'Password is required';
      if (form.password.length < 8) return 'Password must be at least 8 characters';
      if (form.password !== form.confirmPassword) return 'Passwords do not match';
    }
    return '';
  };

  const next = () => {
    const validationError = validateStep();
    if (validationError) { setError(validationError); return; }
    setError('');
    if (step < steps.length - 2) {
      setStep((s) => s + 1);
    } else {
      handleFinish();
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    setError('');

    try {
      // Call edge function — uses service role, bypasses email rate limits
      const res = await fetch(REGISTER_EDGE_FN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          agency_name: form.agencyName.trim(),
          owner_name: form.fullName.trim(),
          owner_email: form.email.trim().toLowerCase(),
          password: form.password,
          phone: form.phone.trim(),
          website: form.website.trim(),
          country: form.country,
          city: form.city.trim(),
          plan: form.plan,
          brand_color: form.brandColor,
          currency: form.currency,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Registration failed. Please try again.');
      }

      // Sign in the user with their credentials (account is already confirmed)
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });

      if (signInError) {
        // Account created but sign-in failed — send them to login
        setRegisteredAgencyName(form.agencyName);
        setLoading(false);
        setStep(3);
        return;
      }

      setRegisteredAgencyName(form.agencyName);
      setLoading(false);
      setStep(3);
    } catch (err: unknown) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    }
  };

  const goToDashboard = () => navigate('/dashboard');

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: brand }}>
            <i className="ri-plane-line text-white text-xl" />
          </div>
          <div>
            <p className="font-bold text-gray-800 text-lg leading-none">Travel Booking AI</p>
            <p className="text-xs text-gray-400">Agency Registration</p>
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                style={
                  i <= step
                    ? { backgroundColor: brand, color: '#fff' }
                    : { backgroundColor: '#F1F5F9', color: '#9CA3AF' }
                }
              >
                {i < step ? <i className="ri-check-line text-sm" /> : i + 1}
              </div>
              <span className={`mx-2 text-sm font-medium ${i === step ? 'text-gray-800' : 'text-gray-400'}`}>{s}</span>
              {i < steps.length - 1 && (
                <div className="w-8 h-0.5 mx-1" style={{ backgroundColor: i < step ? brand : '#E5E7EB' }} />
              )}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-gray-100 p-8">

          {/* Error Banner */}
          {error && (
            <div className="mb-5 bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-400" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          {/* Step 0: Agency Info */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Tell us about your agency</h2>
                <p className="text-sm text-gray-400 mt-0.5">Your agency details for the platform</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Agency Name *</label>
                  <input
                    type="text"
                    value={form.agencyName}
                    onChange={(e) => update('agencyName', e.target.value)}
                    placeholder="SkyWave Travel LLC"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Country *</label>
                  <select
                    value={form.country}
                    onChange={(e) => update('country', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
                  >
                    <option value="">Select country</option>
                    {['United Arab Emirates','Saudi Arabia','Qatar','Kuwait','Bahrain','Oman','United Kingdom','USA','India','Pakistan','Egypt','Turkey','Singapore','Australia'].map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">City *</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => update('city', e.target.value)}
                    placeholder="Dubai"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="+971 50 123 4567"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Website</label>
                  <input
                    type="text"
                    value={form.website}
                    onChange={(e) => update('website', e.target.value)}
                    placeholder="www.skywave.ae"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
              </div>
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">Choose Plan *</label>
                <div className="grid grid-cols-3 gap-3">
                  {plans.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => update('plan', p.id)}
                      className={`relative text-left p-4 rounded-xl border-2 cursor-pointer transition-all ${form.plan === p.id ? 'border-teal-400' : 'border-gray-100 hover:border-gray-200'}`}
                    >
                      {p.popular && (
                        <span
                          className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-white px-2.5 py-0.5 rounded-full whitespace-nowrap"
                          style={{ backgroundColor: brand }}
                        >
                          MOST POPULAR
                        </span>
                      )}
                      <p className="font-semibold text-gray-800 text-sm">{p.name}</p>
                      <p className="text-xs font-bold mt-0.5" style={{ color: brand }}>{p.price}</p>
                      <ul className="mt-2 space-y-1">
                        {p.features.map((f) => (
                          <li key={f} className="text-xs text-gray-500 flex items-center gap-1">
                            <i className="ri-check-line text-green-500" />{f}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Admin Account */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Create your admin account</h2>
                <p className="text-sm text-gray-400 mt-0.5">This will be the Agency Owner account</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(e) => update('fullName', e.target.value)}
                    placeholder="Mohammed Al-Khalid"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Work Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="you@youragency.com"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => update('password', e.target.value)}
                      placeholder="Min. 8 characters"
                      className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-5 h-5 flex items-center justify-center"
                    >
                      <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                  {form.password && (
                    <div className="mt-1.5 flex gap-1">
                      {[1,2,3,4].map((i) => (
                        <div
                          key={i}
                          className="h-1 flex-1 rounded-full transition-colors"
                          style={{
                            backgroundColor:
                              form.password.length >= i * 3
                                ? i <= 1 ? '#EF4444' : i <= 2 ? '#F59E0B' : i <= 3 ? '#3B82F6' : '#10B981'
                                : '#E5E7EB',
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password *</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={form.confirmPassword}
                      onChange={(e) => update('confirmPassword', e.target.value)}
                      placeholder="Repeat password"
                      className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-5 h-5 flex items-center justify-center"
                    >
                      <i className={showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                  {form.confirmPassword && (
                    <p className={`text-xs mt-1 ${form.password === form.confirmPassword ? 'text-green-500' : 'text-red-400'}`}>
                      {form.password === form.confirmPassword ? (
                        <><i className="ri-check-line" /> Passwords match</>
                      ) : (
                        <><i className="ri-close-line" /> Passwords do not match</>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-500 flex items-start gap-2">
                <i className="ri-shield-check-line text-teal-500 mt-0.5" />
                <span>Your account will be created instantly — no email confirmation required. You can log in right after registration.</span>
              </div>
            </div>
          )}

          {/* Step 2: Branding */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Customize your branding</h2>
                <p className="text-sm text-gray-400 mt-0.5">Your agency colors and currency settings</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Brand Color</label>
                <div className="flex flex-wrap gap-2">
                  {colorPresets.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => update('brandColor', c)}
                      className="w-10 h-10 rounded-xl cursor-pointer transition-transform hover:scale-110 flex items-center justify-center"
                      style={{ backgroundColor: c }}
                    >
                      {form.brandColor === c && <i className="ri-check-line text-white text-base" />}
                    </button>
                  ))}
                  <input
                    type="color"
                    value={form.brandColor}
                    onChange={(e) => update('brandColor', e.target.value)}
                    className="w-10 h-10 rounded-xl cursor-pointer border-0 p-0"
                  />
                </div>
                <div className="mt-4 p-4 rounded-xl border border-gray-100 flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: form.brandColor }}
                  >
                    {form.agencyName?.slice(0, 2).toUpperCase() || 'SW'}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{form.agencyName || 'Your Agency'}</p>
                    <p className="text-xs" style={{ color: form.brandColor }}>Brand preview</p>
                  </div>
                  <button
                    className="ml-auto text-sm font-medium px-4 py-2 rounded-xl text-white whitespace-nowrap cursor-pointer"
                    style={{ backgroundColor: form.brandColor }}
                  >
                    Sample Button
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Default Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => update('currency', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
                >
                  {['AED','SAR','QAR','KWD','BHD','OMR','USD','GBP','EUR','INR','EGP','SGD'].map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 text-xs text-teal-700 flex items-start gap-2">
                <i className="ri-information-line mt-0.5" />
                <span>You can update your branding, logo, and settings anytime from the Settings page after registration.</span>
              </div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div className="text-center py-8">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: `${brand}15` }}
              >
                <i className="ri-checkbox-circle-line text-5xl" style={{ color: brand }} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">You&apos;re all set!</h2>
              <p className="text-gray-400 text-sm max-w-xs mx-auto mb-6">
                <strong>{registeredAgencyName}</strong> is ready. Welcome to Travel Booking AI — your 14-day free trial has started.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 text-left mb-6 space-y-2">
                <p className="text-xs font-semibold text-gray-600 mb-2">What&apos;s next:</p>
                {[
                  { icon: 'ri-whatsapp-line', text: 'Connect your WhatsApp number' },
                  { icon: 'ri-user-add-line', text: 'Add your first lead' },
                  { icon: 'ri-file-text-line', text: 'Send your first quote' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2 text-xs text-gray-500">
                    <i className={`${item.icon} text-teal-500`} />
                    {item.text}
                  </div>
                ))}
              </div>
              <button
                onClick={goToDashboard}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm cursor-pointer transition-opacity hover:opacity-90 whitespace-nowrap"
                style={{ backgroundColor: brand }}
              >
                Go to Dashboard <i className="ri-arrow-right-line" />
              </button>
              <a href="/login" className="block text-sm text-gray-400 cursor-pointer hover:text-gray-600 mt-3">
                Back to Login
              </a>
            </div>
          )}

          {/* Nav Buttons */}
          {step < 3 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-50">
              <button
                onClick={() => { setError(''); if (step > 0) setStep((s) => s - 1); }}
                disabled={step === 0}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />Back
              </button>
              <button
                onClick={next}
                disabled={loading}
                className="flex items-center gap-2 text-sm font-semibold text-white px-6 py-2.5 rounded-xl cursor-pointer disabled:opacity-70 transition-opacity hover:opacity-90 whitespace-nowrap"
                style={{ backgroundColor: brand }}
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin" /> Creating Agency...</>
                ) : step === 2 ? (
                  <>Create Agency <i className="ri-checkbox-circle-line" /></>
                ) : (
                  <>Next <i className="ri-arrow-right-line" /></>
                )}
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Already have an account?{' '}
          <a href="/login" className="font-semibold cursor-pointer hover:underline" style={{ color: brand }}>Sign in</a>
        </p>
      </div>
    </div>
  );
}
